import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PreInvoicesApiAdapter } from '../adapters/PreInvoicesApiAdapter';

/**
 * useBulkInvoicePreInvoices - Hook para facturar múltiples prefacturas
 *
 * Usa el mismo método que el desplegable: POST /invoices/pre-invoices/{id}/invoice
 * por cada prefactura seleccionada (PreInvoicesApiAdapter.invoicePreInvoice).
 *
 * @returns {Object} Función bulkInvoicePreInvoices y estado isInvoicing
 */
export const useBulkInvoicePreInvoices = () => {
  const { t } = useTranslation('preInvoicesModule');
  const { toast } = useToast();
  const [isInvoicing, setIsInvoicing] = useState(false);

  const bulkInvoicePreInvoices = async (
    preInvoiceIds: number[],
    onSuccess?: () => void
  ) => {
    if (preInvoiceIds.length === 0) {
      return;
    }

    setIsInvoicing(true);

    const httpClient = createHttpClient(apiClientWH);
    const adapter = new PreInvoicesApiAdapter(httpClient);

    try {
      for (const id of preInvoiceIds) {
        const response = await adapter.invoicePreInvoice(id);
        if (!response.success) {
          toast({
            title: t('toast.bulk_invoice_error.title', 'Error al facturar'),
            description: response.message,
            variant: 'destructive',
          });
          return;
        }
      }

      toast({
        title: t('toast.bulk_invoice_success.title', 'Prefacturas facturadas'),
        description: t('toast.bulk_invoice_success.description', {
          count: preInvoiceIds.length,
          defaultValue: `Se han facturado ${preInvoiceIds.length} prefacturas exitosamente`,
        }),
        variant: 'default',
      });
      onSuccess?.();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        t('toast.bulk_invoice_error.unexpected', 'Error inesperado al facturar las prefacturas.');
      toast({
        title: t('toast.bulk_invoice_error.title', 'Error al facturar'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsInvoicing(false);
    }
  };

  return {
    bulkInvoicePreInvoices,
    isInvoicing,
  };
};

